import { StylesConfig, GroupBase } from 'react-select';

/**
 * Shared styles for react-select components.
 * Provides consistent styling across all select components with dark mode support.
 */
export const getSelectStyles = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  isDarkMode: boolean,
  variant: 'default' | 'compact' = 'default'
): StylesConfig<Option, IsMulti, Group> => {
  const isCompact = variant === 'compact';

  return {
    control: (base, state) => ({
      ...base,
      minHeight: isCompact ? '36px' : '47px',
      borderWidth: '2px',
      borderColor: state.isFocused
        ? '#3b82f6'
        : isDarkMode
        ? '#374151'
        : '#e5e7eb',
      borderRadius: isCompact ? '8px' : '12px',
      backgroundColor: isDarkMode ? '#1f2937' : 'white',
      boxShadow: state.isFocused
        ? `0 0 0 4px ${isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)'}`
        : 'none',
      '&:hover': {
        borderColor: state.isFocused
          ? '#3b82f6'
          : isDarkMode
          ? '#4b5563'
          : '#d1d5db',
      },
    }),
    placeholder: (base) => ({
      ...base,
      color: isDarkMode ? '#9ca3af' : '#6b7280',
      fontSize: '14px',
    }),
    input: (base) => ({
      ...base,
      color: isDarkMode ? '#f9fafb' : '#111827',
      fontSize: '14px',
    }),
    singleValue: (base) => ({
      ...base,
      color: isDarkMode ? '#f9fafb' : '#111827',
      fontSize: '14px',
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: isDarkMode ? '#374151' : '#e5e7eb',
      borderRadius: '6px',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: isDarkMode ? '#f9fafb' : '#111827',
      fontSize: '13px',
      padding: '2px 6px',
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: isDarkMode ? '#9ca3af' : '#6b7280',
      ':hover': {
        backgroundColor: isDarkMode ? '#4b5563' : '#d1d5db',
        color: isDarkMode ? '#f9fafb' : '#111827',
      },
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '12px',
      backgroundColor: isDarkMode ? '#1f2937' : 'white',
      boxShadow: isDarkMode
        ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
        : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      overflow: 'hidden',
      zIndex: 60,
    }),
    menuList: (base) => ({
      ...base,
      padding: 0,
      maxHeight: '200px',
      overflowY: 'auto',
    }),
    option: (base, state) => ({
      ...base,
      fontSize: '14px',
      padding: '12px 16px',
      backgroundColor: state.isSelected
        ? '#3b82f6'
        : state.isFocused
        ? isDarkMode
          ? '#374151'
          : '#eff6ff'
        : isDarkMode
        ? '#1f2937'
        : 'white',
      color: state.isSelected
        ? 'white'
        : isDarkMode
        ? '#f9fafb'
        : '#111827',
      borderBottom: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
      '&:last-child': {
        borderBottom: 'none',
      },
      '&:active': {
        backgroundColor: state.isSelected
          ? '#3b82f6'
          : isDarkMode
          ? '#4b5563'
          : '#dbeafe',
      },
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 60,
    }),
    clearIndicator: (base) => ({
      ...base,
      color: isDarkMode ? '#9ca3af' : '#6b7280',
      ':hover': {
        color: isDarkMode ? '#f9fafb' : '#111827',
      },
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: isDarkMode ? '#9ca3af' : '#6b7280',
      ':hover': {
        color: isDarkMode ? '#f9fafb' : '#111827',
      },
    }),
  };
};
